import * as Papa from 'papaparse';
import type { ParsedData } from '../../../interfaces/toolData';

// Import new modular structure
import type { FileConfig } from './types/FileConfig';
import { 
  findHeaderLineAndTimeColumn, 
  findDataStart
} from './utils/HeaderDetection';
import { parseMetadata } from './utils/MetadataExtraction';
import { SurveyProcessor } from './utils/SurveyProcessor';
import { TimeSeriesProcessor } from './utils/TimeSeriesProcessor';

// Re-export CONFIGS and FileConfig for backward compatibility
export { CONFIGS } from './configs';
export type { FileConfig } from './types/FileConfig';

// Common CSV structure after initial parsing
interface ParsedCSVStructure {
  headers: string[];
  rawData: Record<string, string>[];
  metadata: Record<string, string>;
  timeColumnName: string | null;
  parseErrors: string[];
}

// Extract common CSV parsing logic
function parseCSVStructure(fileContent: string, config: FileConfig): ParsedCSVStructure {
  const lines = fileContent.split('\n');
  
  // Use new flexible timestamp detection
  const { headerLineIndex, timeColumnName } = findHeaderLineAndTimeColumn(lines, config);
  const dataStartIndex = findDataStart(lines, config, config.fileType);
  const metadataLines = lines.slice(0, headerLineIndex);
  const metadata = parseMetadata(metadataLines);
  
  const headerLine = lines[headerLineIndex];
  const headers = headerLine.split(',').map(h => h.trim());
  
  const dataLines = lines.slice(dataStartIndex);
  const dataContent = dataLines.join('\n');
  
  const parseResult = Papa.parse(dataContent, {
    header: false,
    skipEmptyLines: true,
  });
  
  const rawData = (parseResult.data as unknown[][]).map((row: unknown[]) => {
    const rowObject: Record<string, string> = {};
    headers.forEach((header, index) => {
      rowObject[header] = String(row[index] ?? '');
    });
    return rowObject;
  });
  
  return {
    headers,
    rawData,
    metadata,
    timeColumnName,
    parseErrors: parseResult.errors?.map(e => e.message) || []
  };
}





// Convenience function for when you know the file type
export function parseCSVWithConfig(fileContent: string, config: FileConfig, filename?: string): ParsedData {
  // Parse the common CSV structure
  const parsedStructure = parseCSVStructure(fileContent, config);
  
  // Convert based on config data type
  if (config.dataType === 'survey') {
    return SurveyProcessor.process(parsedStructure, config, filename);
  } else {
    return TimeSeriesProcessor.process(parsedStructure, config, filename);
  }
} 